import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  createContext,
} from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
// Copied from app.run.js for fadeout effect
import $ from 'jquery';
import {
  Country,
  OrgSettings,
  AvailabilityHistoryEntry,
  OrgAddress,
  OrgFeatures,
  JsonObject,
} from '@bb/domain/dist/types';
import { NotificationMap } from '@bb/domain/dist/types/AppointmentConfig';
import { ClientContactTag, IGenericContact } from '@bb/contacts';
import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import csrfTokenService from '@app/helpers/csrfTokenService';
import {
  featureFlagClient,
  featureEnabled,
  getOrgLogoSrcFromListing,
} from 'utils';
import { User } from '@bb/users';
import { ListingDoc, OrgDoc } from '@bb/domain/dist/schemas';
import usePusher from '../hooks/usePusher';

export type ContactType =
  | 'agent'
  | 'seller'
  | 'landlord/manager'
  | 'tenant'
  | 'concierge'
  | 'client'
  | 'customagent'
  | 'nonagent';

type ExternalDashUser = IGenericContact & {
  contactType: ContactType;
  buyerNotifications?: NotificationMap;
  buyerEmailDelivery?: boolean;
  tags?: ClientContactTag[];
  country?: Country;
};

export type CoopDashUser = {
  id?: string;
  firstName: string;
  lastName: string;
  name: string;
  org: {
    id?: string;
    name: string;
    address: string;
    phone: string;
    onboardingPortalId: string;
  };
  country: Country;
  email: string;
  uid?: string;
  mls?: string;
  phone: string;
  contactType?: 'coop';
  buyerNotifications?: NotificationMap;
  buyerEmailDelivery?: boolean;
  tags?: ClientContactTag[];
};

export type RetsDashUser = {
  firstName: string;
  name: string;
  org: {
    name: string;
    address: string;
    phone: string;
  };
  country: Country;
  email: string;
  emails: string;
  uid: string;
  mls?: string;
  phone: string;
  buyerNotifications?: NotificationMap;
  buyerEmailDelivery?: boolean;
  tags?: ClientContactTag[];
};

export type ExternalListing = JsonObject<
  Omit<
    ListingDoc,
    | 'availability_history'
    | 'visibility'
    | 'pageStatus'
    | 'brokerage_remarks'
    | 'org'
    | 'reps'
  > & {
    reps: User[];
    org: {
      id: string;
      addresses: {
        [key: string]: OrgAddress[];
        [key: number]: OrgAddress[];
      };
      name: string;
      customLogin: string;
      settings: OrgSettings;
      features: OrgFeatures[];
      logoUrl: string;
      onboardingPortalId: string;
    };
    imageUrls: string[];
    canEdit: false;
    canRead: false;
    canChangeStatus: false;
    mls_numbers_formatted?: string;
    dom: string;
    leased_details?: AvailabilityHistoryEntry;
    sold_details?: AvailabilityHistoryEntry;
    appointmentCount?: number;
    isUnclaimed?: boolean;
  }
>;

export type ExternalAuthType = {
  user:
    | JsonObject<ExternalDashUser>
    | JsonObject<CoopDashUser>
    | JsonObject<RetsDashUser>;
  coop?:
    | JsonObject<ExternalDashUser>
    | JsonObject<CoopDashUser>
    | JsonObject<RetsDashUser>;
  orgLogo: string;
  orgId: string;
  ownerOrg?: JsonObject<OrgDoc>;
  dashboardId?: string;
  retsId?: string;
  // Client Dashboard will return 'listings' but Coop Dashboard will return 'listing'
  listing?: ExternalListing;
  listings?: ExternalListing[];
  buyerShowingsCount?: number;
  uid?: string;
  coopDashboard?: boolean;
  externalDashboard?: boolean;
  retsDashboard?: boolean;
  hasFeature: (feature: OrgFeatures) => boolean;
  hasBookedShowing?: boolean;
};

export type ExternalAuthContextType = {
  auth?: ExternalAuthType;
  setAuth?: React.Dispatch<React.SetStateAction<ExternalAuthType>>;
};
const ExternalAuthContext = createContext<ExternalAuthContextType>({} as any);

export const ExternalAuthContextProvider: React.FC = ({ children }) => {
  const [auth, setAuth] = useState<ExternalAuthType>();
  const [hasBookedShowing, setHasBookedShowing] = useState<boolean>(false);
  const location = useLocation();
  const history = useHistory();

  const [isInitialized, setIsInitialized] = useState(false);
  const isMounted = React.useRef(true);

  usePusher();

  const [identifier, externalId] = useMemo(() => {
    const { pathname, search } = location;

    // eslint-disable-next-line prefer-const
    let [, _identifier, _externalId] = pathname.split('/');

    // For old emails where the external link had the rets/:orgId/?retsId=v2--... format
    if (_externalId && !['v2', 'v3'].includes(_externalId.slice(0, 2))) {
      _externalId = search?.split(/[=&]/).find(part => part.startsWith('v2--'));
    }

    return [_identifier, _externalId];
  }, [location]);

  const fetchWithDashboardId = useCallback(async (dashboardId: string) => {
    const listingIdParam =
      window.location.href.split('/listing/')[1] ||
      window.location.href.split('?listingId=')[1] ||
      '';

    const appointmentIdParam =
      window.location.href.split('/appointments/')[1]?.substring(0, 24) ||
      window.location.href.split('/showing/')[1]?.substring(0, 24) ||
      '';

    try {
      const {
        data: {
          user,
          listing,
          listings,
          org,
          type,
          newDashboardId,
          listingId,
          filteredFeatures,
          buyerShowingsCount,
        },
      } = await axios.get<
        JsonObject<{
          user: ExternalDashUser | CoopDashUser;
          listing?: ExternalListing;
          listings?: ExternalListing[];
          org?: OrgDoc;
          type: 'coop' | 'external';
          newDashboardId?: string;
          listingId?: string;
          filteredFeatures: OrgFeatures[];
          buyerShowingsCount?: number;
        }>
      >(
        `/external/dash/${dashboardId}${
          listingIdParam
            ? `?listingId=${listingIdParam}`
            : `?appointmentId=${appointmentIdParam}`
        }`,
      );

      if (newDashboardId) {
        const newPath = window.location.href.replace(
          dashboardId,
          newDashboardId,
        );

        /**
         * Handle redirect from old seller/tenant/landlord dashboard to
         * new client dashboard FE routes and vise versa, for listing, showings, & notifications pages
         * NOTE: listingId is only returned when redirecting from OLD to NEW dashboard
         * */
        if (newPath.includes('/listing/') && listingId) {
          window.location.href = `${newPath}${listingId}`;
          return;
        }
        if (newPath.includes('/listing/') && !listingId) {
          const splitPath = newPath.split('/');
          splitPath.pop();
          window.location.href = `${splitPath.join('/')}`;
          return;
        }
        if (
          newPath.includes('/appointments') &&
          !newPath.match(/\/appointments\/[a-z0-9]{24}/) &&
          listingId
        ) {
          window.location.href = `${newPath}/listing/${listingId}`;
          return;
        }
        if (newPath.includes('/appointments') && !listingId) {
          const index = newPath.indexOf('/listing');
          if (index > 0) {
            window.location.href = `${newPath.slice(0, index)}`;
            return;
          }
        }
        if (newPath.includes('/notifications') && listingId) {
          window.location.href = `${newPath}?listingId=${listingId}`;
          return;
        }
        if (newPath.includes('/notifications') && !listingId) {
          const index = newPath.indexOf('listingId=');
          if (index > 0) {
            window.location.href = `${newPath.slice(0, index)}`;
            return;
          }
        }

        window.location.href = newPath;
        return;
      }

      if (!isMounted.current) return;

      const formattedAuth: ExternalAuthType = {
        user,
        orgLogo:
          user.contactType !== 'client'
            ? `${getOrgLogoSrcFromListing(listing)}`
            : org.logoUrl,
        orgId: user.contactType !== 'client' ? listing.org.id : org.id,
        ownerOrg: org,
        dashboardId,
        listing,
        listings:
          listings?.sort(
            (a, b) =>
              Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)),
          ) || [],
        buyerShowingsCount,
        hasFeature: (feature: OrgFeatures) =>
          filteredFeatures.includes(feature),
      };

      if (type === 'coop') {
        formattedAuth.coopDashboard = true;
      } else {
        formattedAuth.externalDashboard = true;
      }

      setAuth(formattedAuth);
    } catch (err) {
      errorReporter.error(err);
      Swal.fire({
        title: 'Invalid dashboard',
        text: 'An error occurred, please refresh the page. If the error still persists, please contact the office directly.',
        type: 'error',
      });
    } finally {
      $('.loader-container').fadeOut(); // will first fade out the loading animation
    }
  }, []);

  const fetchWithRetsId = useCallback(async (retsId: string) => {
    try {
      const {
        data: { user, listing, filteredFeatures },
      } = await axios.get<{
        user: RetsDashUser;
        listing: ExternalListing;
        filteredFeatures: OrgFeatures[];
      }>(`/external/user/${retsId}`);

      if (!isMounted.current) return;

      const formattedAuth: ExternalAuthType = {
        user,
        coop: user,
        orgLogo: `${getOrgLogoSrcFromListing(listing)}`,
        orgId: listing.org.id,
        retsId,
        listing,
        uid: user.uid,
        retsDashboard: true,
        hasFeature: (feature: OrgFeatures) =>
          filteredFeatures.includes(feature),
      };

      setAuth(formattedAuth);
    } catch (err) {
      errorReporter.error(err);
      Swal.fire({
        title: 'Invalid dashboard',
        text: 'An error occurred, please refresh the page. If the error still persists, please contact the office directly.',
        type: 'error',
      });
    } finally {
      $('.loader-container').fadeOut(); // will first fade out the loading animation
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        featureFlagClient.initialize(),
        csrfTokenService.initializeToken(),
      ]);
      setIsInitialized(true);
    };

    initialize();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    if (!window.location.pathname.includes('/app/external')) {
      setAuth(undefined);
      let dashboardId: string | undefined;
      let retsId: string | undefined;
      if (identifier === 'dash') {
        dashboardId = externalId;
      }
      if (identifier === 'rets') {
        retsId = externalId;
      }
      if (dashboardId) {
        fetchWithDashboardId(dashboardId);
      } else if (retsId) {
        fetchWithRetsId(retsId);
      } else {
        setAuth(undefined);
        $('.loader-container').fadeOut(); // fade out app-wide loader for component loader
      }
    }
  }, [
    isInitialized,
    identifier,
    externalId,
    fetchWithDashboardId,
    fetchWithRetsId,
  ]);

  useEffect(() => {
    if (!isInitialized) return;

    if (!auth?.listing?.isUnclaimed) return;
    if (identifier === 'dash') {
      if (
        location.pathname.includes('/appointments') ||
        location.pathname.includes('/offers') ||
        location.pathname.includes('/book')
      ) {
        history.replace(`/dash/${externalId}/listing`);
      }
    }

    if (identifier === 'rets') {
      if (
        location.pathname.includes('/appointments/book') ||
        location.pathname.includes('/appointments/offer')
      ) {
        history.replace(`/rets/${externalId}/listing`);
      }
    }
  }, [
    isInitialized,
    history,
    identifier,
    externalId,
    location.pathname,
    auth?.listing?.isUnclaimed,
  ]);

  useEffect(() => {
    (async () => {
      try {
        let _hasBookedShowing;
        if (identifier === 'rets') {
          ({
            data: { hasBookedShowing: _hasBookedShowing },
          } = await axios.get<{ hasBookedShowing: boolean }>(
            `/external/rets/${externalId}/has_booked_showing`,
          ));
        } else {
          ({
            data: { hasBookedShowing: _hasBookedShowing },
          } = await axios.get<{ hasBookedShowing: boolean }>(
            `/external/dash/${externalId}/has_booked_showing`,
          ));
        }
        setHasBookedShowing(_hasBookedShowing);
      } catch (err) {
        window.location.href = '/invalid_dashboard_error';
      }
    })();
  }, []);

  useEffect(() => {
    if (!auth || auth.hasBookedShowing) return;
    if (auth.hasBookedShowing !== hasBookedShowing) {
      setAuth((_auth: ExternalAuthType) => ({
        ..._auth,
        hasBookedShowing,
      }));
    }
  }, [auth, hasBookedShowing]);

  return (
    <ExternalAuthContext.Provider
      value={{
        auth,
        setAuth,
      }}
    >
      {children}
    </ExternalAuthContext.Provider>
  );
};

export default ExternalAuthContext;
